import { useState } from 'react';
import { colour, space, text } from '../design/tokens.ts';
import { Button, Card, Field, Page, Select, Status, TextInput, Thumb, Tile, Tiles } from '../components/basics.tsx';
import { CouldNotLoad, DoneToast, Empty, Loading } from '../components/states.tsx';
import { Dialog } from '../components/Dialog.tsx';
import { Briefs } from './vcs/Briefs.tsx';
import { api } from '../api/client.ts';
import { useLoad } from '../app/useLoad.ts';
import { useCan } from '../app/me.tsx';

/**
 * Screen 11: every virtual camera in one splat.
 *
 * Per splat, because a VC belongs to a splat scene — `cam0003` in one splat is
 * not `cam0003` in another, and names are only unique within a splat.
 *
 * "Plan new VCs" shows the numbers before it makes them, because an id is fixed
 * forever and never reused: planning three by accident spends three ids
 * permanently.
 */

interface VcRow {
  _id: string;
  vc_id: string;
  name?: string;
  view: string | null;
  sequences: number;
  setup: { state: 'planned' | 'ready' | 'stale'; assigned_to?: string; due?: string };
  action: { key: 'ready' | 'created'; says: string } | null;
  says: string;
}

interface VcTable {
  rows: VcRow[];
  tiles: { planned: number; ready: number; stale: number };
  total: number;
  room_for: number;
}

export function Vcs({ projectId }: { projectId: string }) {
  const can = useCan();
  const [splatId, setSplatId] = useState<string>('');
  const [filterState, setFilterState] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [renaming, setRenaming] = useState<{ vc: VcRow; name: string } | null>(null);
  const [planning, setPlanning] = useState<{ howMany: number; assignee: string } | null>(null);

  const { state: splats } = useLoad<{ splats: { splat_id: string; says: string; version: number }[] }>(
    () => api.get(`/api/splats?project_id=${encodeURIComponent(projectId)}`),
    [projectId]
  );
  const chosen = splatId || splats.data?.splats[0]?.splat_id || '';

  // No made-up table when there is no splat. It used to resolve to an empty
  // one with `room_for: 50`, which drew four tiles and an enabled button for
  // a splat that does not exist — and then planning silently did nothing,
  // because there was nothing to plan *into*.
  const { state, reload } = useLoad<VcTable | null>(
    () => (chosen ? api.get<VcTable>(`/api/splats/${chosen}/vcs/table`) : Promise.resolve(null)),
    [chosen]
  );
  const { state: preview } = useLoad<{ will_create: string[]; room_for: number; refused: string | null } | null>(
    () =>
      chosen && planning
        ? api.get(`/api/splats/${chosen}/vcs/plan?how_many=${planning.howMany}`)
        : Promise.resolve(null),
    [chosen, planning?.howMany]
  );

  const table = state.data ?? null;
  const rows = (table?.rows ?? []).filter((r) => !filterState || r.setup.state === filterState);
  /** The splat list came back and there was nothing in it. */
  const noSplat = splats.status === 'ready' && (splats.data?.splats.length ?? 0) === 0;

  const markReady = async (vc: VcRow): Promise<void> => {
    try {
      await api.post(`/api/vcs/${vc._id}/ready`, {});
      setToast(`${vc.name ?? vc.vc_id} is placed in the current splat. Its passes are unblocked.`);
      reload();
    } catch (err) {
      setToast(err instanceof Error ? err.message : 'That did not work.');
    }
  };

  const rename = async (): Promise<void> => {
    if (!renaming) return;
    try {
      await api.patch(`/api/vcs/${renaming.vc._id}/name`, { name: renaming.name.trim() });
      setRenaming(null);
      setToast('Renamed. The number never changes.');
      reload();
    } catch (err) {
      setToast(err instanceof Error ? err.message : 'That name was refused.');
    }
  };

  const plan = async (): Promise<void> => {
    if (!planning) return;
    if (!chosen) {
      // Unreachable now the button is gated, but a silent return is what made
      // this look broken in the first place, so it says something instead.
      setToast('There is no splat in this project yet, so there is nothing to put a camera in.');
      return;
    }
    try {
      const made = await api.post<{ planned: string[] }>(`/api/splats/${chosen}/vcs/plan`, {
        how_many: planning.howMany,
        project_id: projectId,
        ...(planning.assignee ? { assigned_to: planning.assignee } : {})
      });
      setPlanning(null);
      setToast(`Planned ${made.planned.join(', ')}.`);
      reload();
    } catch (err) {
      setToast(err instanceof Error ? err.message : 'Those could not be planned.');
    }
  };

  return (
    <Page
      eyebrow="MAESTRO · VIRTUAL CAMERAS"
      title="Virtual cameras"
      description="Cameras placed inside a splat scene. The number is fixed forever and never reused; the name is optional and can change."
      action={
        can.assign_vc ? (
          <Button
            tone="primary"
            onClick={() => setPlanning({ howMany: 1, assignee: '' })}
            disabled={!chosen || table == null || table.room_for === 0}
            disabledReason={
              !chosen
                ? 'This project has no splat yet, so there is nowhere to put a camera.'
                : table == null
                  ? 'Still loading.'
                  : 'This splat is full.'
            }
          >
            Plan new VCs
          </Button>
        ) : undefined
      }
    >
      {(splats.data?.splats.length ?? 0) > 1 && (
        <Card>
          <Field label="Which splat">
            <Select
              value={chosen}
              onChange={setSplatId}
              options={(splats.data?.splats ?? []).map((s) => ({
                value: s.splat_id,
                label: `${s.says} · version ${s.version}`
              }))}
            />
          </Field>
        </Card>
      )}

      {table && (
        <Tiles>
          {(['ready', 'stale', 'planned'] as const).map((key) => (
            <Tile
              key={key}
              count={table.tiles[key]}
              label={key === 'ready' ? 'Placed in the current splat' : key === 'stale' ? 'Setup needs rechecking' : 'Not made yet'}
              on={filterState === key}
              onClick={() => setFilterState(filterState === key ? null : key)}
            />
          ))}
          <Tile count={table.room_for} label="More this splat can hold" />
        </Tiles>
      )}

      {/* A VC lives inside a splat, so a project without one has no VCs to
          show. It can still be *planned*, though, which is most of what
          happens before a shoot — so this says where things stand and then
          gets out of the way of the planning list below. */}
      {noSplat && (
        <Card>
          <span style={{ ...text.small, color: colour.inkSoft }}>
            This project has no splat yet, so there is nowhere to place a camera. A splat arrives
            from a capture. Until then you can plan the cameras you mean to build.
          </span>
        </Card>
      )}

      {!noSplat && state.status === 'loading' && !table && (
        <Loading says="Loading the virtual cameras…" rows={5} />
      )}
      {!noSplat && state.status === 'failed' && !table && (
        <CouldNotLoad what="the virtual cameras" error={state.error} onRetry={reload} />
      )}
      {table && rows.length === 0 && (
        <Empty
          title={table.total === 0 ? 'No virtual cameras yet' : 'None of those'}
          says={
            table.total === 0
              ? 'Plan some, assign them to somebody, and they appear here as "not made yet" until that person places them.'
              : 'Pick another tile to see more.'
          }
        />
      )}

      {rows.map((vc) => (
        <Card key={vc._id}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: space.control, alignItems: 'center' }}>
            <Thumb url={vc.view} alt={`the view from ${vc.vc_id}`} size={96} />
            <div style={{ flex: '1 1 280px', display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
              <span style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: space.tight }}>
                <span style={text.monoStrong}>{vc.vc_id}</span>
                {vc.name && <span style={text.bodyStrong}>{vc.name}</span>}
                <Status value={vc.setup.state} />
              </span>
              <span style={{ ...text.small, color: colour.inkSoft }}>{vc.says}</span>
              <span style={{ ...text.note, color: colour.inkMuted }}>
                used by {vc.sequences} {vc.sequences === 1 ? 'sequence' : 'sequences'}
                {vc.setup.assigned_to ? ` · waiting on ${vc.setup.assigned_to}` : ''}
              </span>
            </div>
            {can.assign_vc && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: space.tight }}>
                <Button onClick={() => setRenaming({ vc, name: vc.name ?? '' })}>Rename</Button>
                {vc.action && (
                  <Button tone="primary" onClick={() => void markReady(vc)}>
                    {vc.action.says}
                  </Button>
                )}
              </div>
            )}
          </div>
        </Card>
      ))}

      {renaming && (
        <Dialog
          title={`Rename ${renaming.vc.vc_id}`}
          says="The number never changes — it is fixed forever and never reused. A name already used in this splat is refused."
          confirm="Rename"
          onConfirm={() => void rename()}
          onClose={() => setRenaming(null)}
        >
          <Field label="Name (letters, numbers and underscores)">
            <TextInput
              value={renaming.name}
              onChange={(name) => setRenaming({ ...renaming, name })}
              placeholder="hero_wide"
            />
          </Field>
        </Dialog>
      )}

      {planning && (
        <Dialog
          title="Plan new virtual cameras"
          says="An id is fixed forever once made, so these are the exact numbers that will be spent."
          confirm={preview.data?.refused ? 'Cannot' : `Plan ${planning.howMany}`}
          onConfirm={() => void plan()}
          onClose={() => setPlanning(null)}
        >
          <Field label="How many">
            <TextInput
              value={String(planning.howMany)}
              onChange={(v) => setPlanning({ ...planning, howMany: Math.max(1, Number(v) || 1) })}
            />
          </Field>
          <Field label="Assign to (optional)">
            <TextInput
              value={planning.assignee}
              onChange={(assignee) => setPlanning({ ...planning, assignee })}
              placeholder="Kcross"
            />
          </Field>
          {preview.data?.refused ? (
            <span style={{ ...text.small, color: colour.staleInk }}>{preview.data.refused}</span>
          ) : (
            <span style={{ ...text.small, color: colour.inkSoft }}>
              This will create{' '}
              <strong style={text.monoStrong}>
                {preview.data?.will_create[0]}
                {(preview.data?.will_create.length ?? 0) > 1
                  ? ` to ${preview.data?.will_create[preview.data.will_create.length - 1]}`
                  : ''}
              </strong>
              , and leave room for {(preview.data?.room_for ?? 0) - planning.howMany} more.
            </span>
          )}
        </Dialog>
      )}

      {/* Planned cameras, below whatever is already built. Shown whether or
          not there is a splat, because planning is what happens first. */}
      <Briefs
        projectId={projectId}
        canPlan={can.assign_vc}
        splats={(splats.data?.splats ?? []).map((sp) => ({
          splat_id: sp.splat_id,
          says: sp.says,
          version: sp.version
        }))}
        onBuilt={reload}
      />

      {toast && <DoneToast says={toast} onClose={() => setToast(null)} />}
    </Page>
  );
}
